// Reliable sync of the Local Storage watch-time buffer to the backend.
//
// Local Storage is the source of truth; the network is best-effort. Every trigger below just
// calls syncPending(), which is safe to run any number of times because the server upserts
// cumulative per-session totals (it keeps the greatest value, so replays never double-count):
//   - while playing (checkpoint), on pause / ended / player close (from useVideoWatchTracker)
//   - page hide (keepalive fetch, best effort - the stored entry is the real guarantee)
//   - app start / login (startWatchTimeSync), the browser `online` event, and a 30s retry timer
//
// Entries are only ever sent for the CURRENTLY logged-in student: the server attributes data
// to the JWT user, so another student's leftovers must wait for their own login.

import Cookies from "js-cookie";
import { decodeToken, isTokenValid } from "./authSession";
import {
	clearTab,
	isTabAlive,
	listEntries,
	newSessionId,
	purgeExpired,
	readEntry,
	removeEntry,
	touchTab,
} from "./watchTimeStore";
import { syncWatchTimeService } from "../services/lessons/syncWatchTimeService";
import { watchLog } from "./watchLog";

// identifies this page load; entries owned by other live tabs are left to those tabs
export const TAB_ID = newSessionId();
// how often this page proves it is alive (see isTabAlive in the store)
const HEARTBEAT_MS = 10 * 1000;
const BATCH_SIZE = 20;
const RETRY_INTERVAL_MS = 30 * 1000;
const MAX_REJECTED_ATTEMPTS = 5;

// sessionId -> seq last acknowledged by the server during this page load
const acked = new Map();
// sessionId -> consecutive non-network failures
const failures = new Map();

let running = null;
let rerunOptions = null;
let backoffUntil = 0;
let backoffStep = 0;
let started = false;

const status = {
	state: "idle", // idle | syncing | ok | offline | auth | error
	lastSyncAt: null,
	lastError: null,
	dropped: 0,
};
const listeners = new Set();

function setStatus(patch) {
	Object.assign(status, patch);
	listeners.forEach((listener) => {
		try {
			listener(getWatchSyncStatus());
		} catch (error) {
			// a broken listener must never break syncing
		}
	});
}

export function subscribeWatchSync(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function getWatchSyncStatus() {
	const studentId = currentStudentId();
	const pending =
		studentId === null
			? 0
			: listEntries(studentId).filter((e) => acked.get(e.sessionId) !== e.seq).length;
	return { ...status, pending };
}

function currentStudentId() {
	const token = Cookies.get("authToken");
	if (!isTokenValid(token)) return null;
	const payload = decodeToken(token);
	return payload && Number.isInteger(payload.id) ? payload.id : null;
}

// an open entry whose owning page is gone (closed tab, crash, reload) can be finished off
const isOrphan = (entry) =>
	!entry.closed && entry.ownerTab !== TAB_ID && !isTabAlive(entry.ownerTab);

function toPayload(entry, forceClosed) {
	return {
		sessionId: entry.sessionId,
		courseId: entry.courseId,
		lessonId: entry.lessonId,
		videoId: entry.videoId,
		startedAt: entry.startedAt,
		endedAt: entry.endedAt,
		watchedSeconds: entry.watchedMs / 1000,
		wallSeconds: entry.wallMs / 1000,
		lastPositionSeconds: entry.lastPositionMs / 1000,
		maxPlaybackRate: entry.maxRate,
		seq: entry.seq,
		closed: Boolean(entry.closed || forceClosed),
	};
}

// which buffered entries this page should send right now
// (an open entry owned by this page is only sent on a checkpoint / flush - not by the
// background retry timer, so a playing session is not sent twice per interval)
function selectEntries(studentId, now, includeOwnOpen) {
	return listEntries(studentId).filter((entry) => {
		if (acked.get(entry.sessionId) === entry.seq && !entry.closed) return false;
		if (entry.closed && acked.get(entry.sessionId) === entry.seq) return true; // ack'd: just cleanup
		return (includeOwnOpen && entry.ownerTab === TAB_ID) || entry.closed || isOrphan(entry);
	});
}

function noteNetworkFailure(error) {
	watchLog("sync failed, kept in Local Storage, will retry:", (error && error.message) || error);
	backoffStep = Math.min(backoffStep + 1, 6);
	const delay = Math.min(1000 * 2 ** backoffStep, 60000) * (0.75 + Math.random() * 0.5);
	backoffUntil = Date.now() + delay;
	setStatus({ state: "offline", lastError: (error && error.message) || "network error" });
}

async function runOnce({ force, keepalive, own }) {
	const studentId = currentStudentId();
	if (studentId === null) {
		setStatus({ state: "auth" });
		return;
	}
	if (!force && Date.now() < backoffUntil) return;

	// bounded: a backlog is drained in batches, but a misbehaving response can never loop forever
	for (let batch = 0; batch < 10; batch++) {
		const now = Date.now();
		const entries = selectEntries(studentId, now, force || own).slice(0, BATCH_SIZE);
		// already-acked closed entries only need local cleanup, no request
		const toClean = entries.filter((e) => e.closed && acked.get(e.sessionId) === e.seq);
		toClean.forEach((e) => removeEntry(e.studentId, e.sessionId));
		const toSend = entries.filter((e) => !toClean.includes(e));
		if (toSend.length === 0) {
			if (status.state === "syncing") setStatus({ state: "ok" });
			return;
		}

		setStatus({ state: "syncing" });
		watchLog(
			`sync -> sending ${toSend.length} session(s)`,
			toSend.map((e) => `${e.sessionId.slice(0, 8)}:${(e.watchedMs / 1000).toFixed(2)}s${e.closed ? " closed" : ""}`)
		);
		const sentClosed = new Map(toSend.map((e) => [e.sessionId, Boolean(e.closed || isOrphan(e))]));
		let data;
		try {
			data = await syncWatchTimeService(
				toSend.map((e) => toPayload(e, isOrphan(e))),
				{ keepalive }
			);
		} catch (error) {
			const httpStatus = error && error.response && error.response.status;
			if (httpStatus === 401) {
				// token expired / invalid: keep everything, retry after the next login
				setStatus({ state: "auth", lastError: "401" });
			} else if (httpStatus && httpStatus >= 400 && httpStatus < 500 && httpStatus !== 429) {
				// the request itself is being refused: do not retry forever
				toSend.forEach((e) => {
					const count = (failures.get(e.sessionId) || 0) + 1;
					failures.set(e.sessionId, count);
					if (count >= MAX_REJECTED_ATTEMPTS) {
						removeEntry(e.studentId, e.sessionId);
						failures.delete(e.sessionId);
						setStatus({ dropped: status.dropped + 1 });
					}
				});
				noteNetworkFailure(error);
				setStatus({ state: "error", lastError: String(httpStatus) });
			} else {
				noteNetworkFailure(error);
			}
			return;
		}

		backoffStep = 0;
		backoffUntil = 0;
		const results = (data && data.results) || [];
		watchLog("sync <- server:", results.map((r) => `${String(r.sessionId).slice(0, 8)} ${r.status}${r.watchedSeconds != null ? " " + r.watchedSeconds + "s" : ""}${r.reason ? " (" + r.reason + ")" : ""}`));
		toSend.forEach((entry) => {
			const result = results.find((r) => r.sessionId === entry.sessionId);
			if (!result) return; // unknown outcome: keep and retry
			failures.delete(entry.sessionId);
			if (result.status === "rejected") {
				// permanent refusal (not enrolled, invalid, ...): retrying cannot help
				removeEntry(entry.studentId, entry.sessionId);
				acked.delete(entry.sessionId);
				setStatus({ dropped: status.dropped + 1, lastError: result.reason || "rejected" });
				return;
			}
			acked.set(entry.sessionId, entry.seq);
			// only discard when the session is finished AND nothing changed while in flight
			const fresh = readEntry(entry.studentId, entry.sessionId);
			if (sentClosed.get(entry.sessionId) && fresh && fresh.seq === entry.seq) {
				removeEntry(entry.studentId, entry.sessionId);
			}
		});
		setStatus({ state: "ok", lastSyncAt: Date.now(), lastError: null });
		if (toSend.length < BATCH_SIZE && entries.length < BATCH_SIZE) return;
	}
}

// Safe to call from anywhere, any number of times. Calls made while a sync is running are
// coalesced into exactly one follow-up run that carries the strongest flags requested
// (otherwise a checkpoint could be swallowed by a weaker timer-triggered run).
// own: also send this page's still-open sessions (checkpoints); the retry timer leaves them out
export function syncPending({ force = false, keepalive = false, own = false } = {}) {
	if (running) {
		rerunOptions = {
			force: Boolean(rerunOptions && rerunOptions.force) || force,
			keepalive: Boolean(rerunOptions && rerunOptions.keepalive) || keepalive,
			own: Boolean(rerunOptions && rerunOptions.own) || own,
		};
		return running;
	}
	running = (async () => {
		try {
			await runOnce({ force, keepalive, own });
			while (rerunOptions) {
				const next = rerunOptions;
				rerunOptions = null;
				await runOnce(next);
			}
		} catch (error) {
			setStatus({ state: "error", lastError: (error && error.message) || "sync error" });
		} finally {
			running = null;
		}
	})();
	return running;
}

// Call once after login / app start (idempotent). Returns a stop function (used by tests).
export function startWatchTimeSync() {
	if (started) {
		// already running (e.g. a new login in the same page): just drain what is waiting
		syncPending({ force: true });
		return () => {};
	}
	started = true;
	purgeExpired();
	const onOnline = () => syncPending({ force: true });
	const onPageHide = () => clearTab(TAB_ID);
	const onPageShow = () => touchTab(TAB_ID);
	window.addEventListener("online", onOnline);
	window.addEventListener("pagehide", onPageHide);
	window.addEventListener("pageshow", onPageShow);
	touchTab(TAB_ID);
	const heartbeat = setInterval(() => touchTab(TAB_ID), HEARTBEAT_MS);
	const timer = setInterval(() => syncPending(), RETRY_INTERVAL_MS);
	syncPending({ force: true });
	return () => {
		started = false;
		window.removeEventListener("online", onOnline);
		window.removeEventListener("pagehide", onPageHide);
		window.removeEventListener("pageshow", onPageShow);
		clearInterval(heartbeat);
		clearInterval(timer);
		clearTab(TAB_ID);
	};
}

// test hook: forget in-memory state between tests
export function __resetWatchSyncForTests() {
	acked.clear();
	failures.clear();
	running = null;
	rerunOptions = null;
	backoffUntil = 0;
	backoffStep = 0;
	started = false;
	Object.assign(status, { state: "idle", lastSyncAt: null, lastError: null, dropped: 0 });
}
